import React, { useEffect, useState, FC } from 'react';
import { Form, Button, Table, Alert } from 'Components/nui';
import { FormWrapper } from 'Components/form';
import { mergeSchema, normalizeChoices } from 'Components/form/utils';
import { LoadingState } from 'Components/Layout';
import { ShowResult } from 'Components/Result';
import { useStoreState } from 'easy-peasy';
import { promisify } from 'es6-promisify';
import { useAsync } from 'react-use';
import { api } from '~/api';
import { submitError, submitSuccess } from 'Components/form/utils';
import { inArrayIf } from '~/utils';
import { useIntl, FormattedMessage } from 'react-intl';
import { useProductById } from '~/hooks/useProducts';
import { WrappedFormUtils } from 'antd/lib/form/Form';

interface Price {
  val: number;
  unit: string;
  reason?: string;
}

interface CustomerPrice {
  id: string;
  name: string;
  price: Price;
}

interface PortPrice {
  id: string;
  country: { name: string };
  port: { name: string };
  zone?: { name: string };
  price: Price;
  count: number;
}

interface CustomerTableProps {
  prices: CustomerPrice[];
}

const CustomerTable: FC<CustomerTableProps> = ({ prices }) => {
  const { formatMessage } = useIntl();
  const columns = [
    {
      title: formatMessage({
        id: 'customer-name',
        defaultMessage: 'Customer name',
      }),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: formatMessage({ id: 'price', defaultMessage: 'Price' }),
      key: 'price',
      align: 'right' as const,
      render: (text: any, record: CustomerPrice) => {
        return record.price.val > 0 ? (
          record.price.val
        ) : (
          <span className="tag">
            {formatMessage({ id: 'n-a', defaultMessage: 'n/a' })}
          </span>
        );
      },
    },
    {
      title: formatMessage({
        id: 'currency-unit',
        defaultMessage: 'Currency/Unit',
      }),
      key: 'currency',
      render: (text: any, record: CustomerPrice) => {
        return record.price.unit;
      },
    },
    {
      title: formatMessage({ id: 'na-reason', defaultMessage: 'N/A reason' }),
      key: 'reason',
      render: (text: any, record: CustomerPrice) => {
        return record.price.reason;
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      className="generic-table"
      columns={columns}
      data={prices}
    />
  );
};

interface PortsTableProps {
  prices: PortPrice[];
  zones: boolean;
}

const PortsTable: FC<PortsTableProps> = ({ prices, zones }) => {
  const { formatMessage } = useIntl();
  const columns = [
    {
      title: formatMessage({ id: 'country', defaultMessage: 'Country' }),
      key: 'country',
      render: (text: any, record: PortPrice) => record.country?.name,
    },
    {
      title: formatMessage({ id: 'port', defaultMessage: 'Port' }),
      key: 'port',
      render: (text: any, record: PortPrice) => record.port?.name,
    },
    ...inArrayIf(zones, {
      title: formatMessage({ id: 'zone', defaultMessage: 'Zone' }),
      key: 'zone',
      render: (_: any, record: PortPrice) => record.zone?.name,
    }),
    {
      title: formatMessage({ id: 'price', defaultMessage: 'Price' }),
      key: 'price',
      align: 'right' as const,
      render: (text: any, record: PortPrice) => {
        return record.price.val > 0 ? (
          record.price.val
        ) : (
          <span className="tag">
            {formatMessage({ id: 'n-a', defaultMessage: 'n/a' })}
          </span>
        );
      },
    },
    {
      title: formatMessage({
        id: 'currency-unit',
        defaultMessage: 'Currency/Unit',
      }),
      key: 'currency',
      render: (text: any, record: PortPrice) => {
        return record.price.unit;
      },
    },
    {
      title: formatMessage({ id: 'na-reason', defaultMessage: 'N/A reason' }),
      key: 'reason',
      render: (text: any, record: PortPrice) => {
        return record.price.reason;
      },
    },
    {
      title: formatMessage({
        id: 'total-customers',
        defaultMessage: 'Total customers',
      }),
      key: 'total',
      render: (text: any, record: PortPrice) => {
        return record.count;
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      className="generic-table"
      columns={columns}
      data={prices}
    />
  );
};

interface Product {
  id: string;
  step: number;
  loading?: Array<{ id: string; desc: string }>;
}

interface Schema {
  [key: string]: any;
}

function getFormWrapper({
  form,
  product,
  schema,
  initialData,
  formatMessage,
}: {
  form: WrappedFormUtils;
  product: Product;
  schema: Schema;
  initialData: any;
  formatMessage: any;
}) {
  const getChoicesByDivision =
    ({ choices }: { choices: any }) =>
    () => {
      const divisionId = form.getFieldValue('division');
      return normalizeChoices(
        Array.isArray(choices) ? choices : (choices?.[divisionId] ?? [])
      );
    };

  let fields = [
    {
      name: 'division',
      choices: normalizeChoices,
    },
    {
      name: 'loading',
      choices: (product.loading || [])
        .map(x => ({
          key: x.id,
          value: x.id,
          label: x.desc,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    {
      name: 'price',
      step: product.step,
      min: product.step,
    },
    {
      name: 'fromaddr',
      choices: getChoicesByDivision,
    },
  ];

  return new FormWrapper(form, initialData, mergeSchema(schema, fields));
}

interface NFormProps {
  form: WrappedFormUtils;
  product: Product;
  schema: Schema;
  submitState: any;
  initialData: any;
  auto?: boolean;
}

function NForm({
  form,
  product,
  schema,
  submitState,
  initialData,
  auto,
}: NFormProps) {
  const { formatMessage } = useIntl();
  const solution = useStoreState(state => state.auth.solution);
  const zones = solution?.freight_types?.includes('zone') ?? false;

  const [prices, setPrices] = useState<
    CustomerPrice[] | PortPrice[] | undefined
  >();
  const [format, setFormat] = useState<'port' | 'customer' | undefined>();
  const validateFieldsAndScroll = promisify(form.validateFieldsAndScroll);

  useEffect(() => {
    if (auto) handleSubmit();
  }, [auto]);

  if (!product) return null;

  const formWrapper = getFormWrapper({
    form,
    product,
    schema,
    initialData,
    formatMessage,
  });

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    try {
      await validateFieldsAndScroll();
    } catch (err) {
      return;
    }
    submitState.setLoading(true);
    const params = formWrapper.serialize();
    try {
      setPrices(undefined);
      const result = await api.getData2(
        `/products/${product.id}/displayed-prices`,
        params
      );
      if (result.format === 'port') {
        setFormat('port');
        const sortedPrices = [...result.prices].sort((a, b) => {
          const countryCompare = (a.country?.name ?? '').localeCompare(
            b.country?.name ?? ''
          );
          if (countryCompare !== 0) return countryCompare;
          return (a.port?.name ?? '').localeCompare(b.port?.name ?? '');
        });
        setPrices(sortedPrices);
      } else if (result.format === 'customer') {
        setFormat('customer');
        setPrices(
          [...result.prices].sort((a, b) => a.name.localeCompare(b.name))
        );
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors?.[0]?.description;
      if (errors) {
        formWrapper.setErrors(errors);
      } else {
        submitState.setResult(err);
      }
    }
    submitState.setLoading(false);
  }

  return (
    <div>
      <div className="customer-prices-form">
        <div className="nui-row">
          <h2 className="light">
            <FormattedMessage
              id="customer-prices"
              defaultMessage="Customer prices"
            />
          </h2>
          <Form className="mt-20">
            {formWrapper.render()}
            <Form.Item className="valign-b">
              <Button
                htmlType="button"
                onClick={handleSubmit}
                loading={submitState.loading}
              >
                <FormattedMessage id="show" defaultMessage="Show" />
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      {prices && (
        <div className="full-page-content">
          <div className="table-wrapper customer-table">
            {prices.length > 0 ? (
              <>
                {format === 'port' && (
                  <PortsTable prices={prices as PortPrice[]} zones={zones} />
                )}
                {format === 'customer' && (
                  <CustomerTable prices={prices as CustomerPrice[]} />
                )}
              </>
            ) : (
              <Alert hasicon>
                <FormattedMessage
                  id="no-data-to-display"
                  defaultMessage="No data to display"
                />
              </Alert>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const WrappedForm = Form.create()(NForm);

interface DisplayedPricesProps {
  productId: string;
  init?: any;
  auto?: boolean;
}

const DisplayedPrices: FC<DisplayedPricesProps> = ({
  productId,
  init,
  auto,
}) => {
  const product = useProductById(productId);

  const schemaState = useAsync(() => {
    if (!productId) return Promise.resolve();
    return api.getData({
      type: 'products',
      id: `${productId}/displayed-prices/schema`,
    });
  }, [productId]);

  return (
    <ShowResult renderSuccess={submitSuccess} renderError={submitError}>
      {submitState => (
        <div className="wrapper">
          <div className="nui-form">
            <LoadingState state={schemaState}>
              {schema => (
                <WrappedForm
                  key="customer-prices"
                  schema={schema}
                  product={product}
                  initialData={init}
                  submitState={submitState}
                  auto={auto}
                />
              )}
            </LoadingState>
          </div>
        </div>
      )}
    </ShowResult>
  );
};

export default DisplayedPrices;
